import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CustomTranslationsService, STATE } from 'src/app/operational-data-common/translations';
import { SUFFIX } from 'src/app/shared/common/units';

import {
  BurnerStatisticsProperties,
  DcTypes,
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  HeatingCascadeTemperatureCommonSupplyCommands,
  HeatingCompressorsProperties,
  HeatingCoolingCircuitRemoteDefrostCommands,
  HeatingCoolingCircuitRemoteDefrostProperties,
  IsActiveProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  NoiseReductionCommands,
  NoiseReductionProperties,
  OffPeakSignal,
  OnOff,
  PvSignalProperties,
  ScheduleCommands,
  ScheduleProperties,
  SetNumberValueCommands,
  SpaceFrostProtectionCommands,
  TemperatureProperty,
  frostProtectionProperties,
  offPeakSignalProperties,
  pvSignal,
  DeviceRoles,
} from '@vi-iot/client';

import {
  convertCelsiusToFahrenheit,
  convertFahrenheitToCelsius,
} from 'src/app/shared/common/units';

import { MatTooltip } from '@angular/material/tooltip';
import { GTMService } from '@services';
import {
  AuthStore,
  DeviceTypes,
  DevicesStore,
  FeatureService,
  GatewayStore,
  InstallationFeaturesStore,
  UnitsStore,
} from '@store';
import { cloneDeep, isNumber } from 'lodash';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import {
  Program,
  ScheduleEntry,
  ScheduleService,
  TimemanagerDialogService,
} from '../../../../features/timemanager/services';

@Component({
  selector: 'app-refrigerant-circuit-details-overlay',
  templateUrl: './refrigerant-circuit-details-overlay.component.html',
  styleUrls: ['./refrigerant-circuit-details-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RefrigerantCircuitDetailsOverlayComponent implements OnInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  STATE = STATE;
  SUFFIX = SUFFIX;
  OnOff = OnOff;
  readonly heatingDevice = DeviceTypes.Heating;
  readonly offPeakSignal = OffPeakSignal;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly suffix = SUFFIX;

  compressorStatistics$: Observable<Feature<BurnerStatisticsProperties, never>>;
  refrigerantCircuitStatus$: Observable<Feature<HeatingCompressorsProperties, never>>;
  noiseReduction$: Observable<Feature<NoiseReductionProperties, NoiseReductionCommands>>;
  noiseReductionSchedule$: Observable<Feature<ScheduleProperties, never>>;
  programs$: BehaviorSubject<Program[]> = new BehaviorSubject<Program[]>([]);
  heatingNoiseReductionMode$: Observable<string>;
  spaceFrostProtection$: Observable<Feature<IsActiveProperty, never>>;
  frostProtection$: Observable<Feature<frostProtectionProperties, never>>;

  airInletTemperature$: Observable<Feature<TemperatureProperty, never>>;
  evaporationTemperature$: Observable<Feature<TemperatureProperty, never>>;
  suctionGasTemperature$: Observable<Feature<TemperatureProperty, never>>;
  suctionGasTemperatureCompressor$: Observable<Feature<TemperatureProperty, never>>;
  lowPressureCompressor$: Observable<Feature<TemperatureProperty, never>>;

  hotGasTemperature$: Observable<Feature<TemperatureProperty, never>>;
  liquidGasTemperature$: Observable<Feature<TemperatureProperty, never>>;
  hotGasTemperatureCompressor$: Observable<Feature<TemperatureProperty, never>>;
  highPressureCompressor$: Observable<Feature<TemperatureProperty, never>>;
  currentThermalPower$: Observable<Feature<IsNumberValueProperty, never>>;
  currentCompressorSpeed$: Observable<Feature<IsNumberValueProperty, never>>;

  fanOneActualValue$: Observable<Feature<IsNumberValueProperty, never>>;
  fanTwoActualValue$: Observable<Feature<IsNumberValueProperty, never>>;
  electronicExpansionValveOneSetpoint$: Observable<Feature<IsNumberValueProperty, never>>;
  electronicExpansionValveOneActualValue$: Observable<Feature<IsNumberValueProperty, never>>;
  electronicExpansionValveTwoSetpoint$: Observable<Feature<IsNumberValueProperty, never>>;
  electronicExpansionValueTwoActualValue$: Observable<Feature<IsNumberValueProperty, never>>;

  isCelsius: boolean;
  availableTemperatureForDropdown: {
    value: number;
    viewValue: string;
  }[];
  availableMinutesForDropdown: {
    value: number;
    viewValue: string;
  }[];
  minutesForDropdownValue: {
    value: number;
    unit?: string;
    type: string;
  };
  temperatureForDropdownValue: {
    value: number;
    unit?: string;
    type: string;
  };
  temperatureValueForRequest: number;
  spaceFrostProtectionConfiguration$: Observable<
    Feature<IsNumberValueProperty, SpaceFrostProtectionCommands>
  >;
  primaryCircuitConfigurationPreVentilation$: Observable<
    Feature<IsNumberValueProperty, HeatingCascadeTemperatureCommonSupplyCommands>
  >;
  pvSignal$: Observable<Feature<PvSignalProperties, never>>;
  offPeakSignal$: Observable<Feature<offPeakSignalProperties, never>>;
  pvAvailable: pvSignal;
  refrigerantCircuitGeneration$: Observable<Feature<IsStringValueProperty, never>>;
  typeElectronicExpansionValve$: Observable<Feature<IsStringValueProperty, never>>;
  ratedOutputCompressorStage$: Observable<Feature<IsNumberValueProperty, SetNumberValueCommands>>;
  heatingCoolingCircuitRemoteDefrost$: Observable<
    Feature<
      HeatingCoolingCircuitRemoteDefrostProperties,
      HeatingCoolingCircuitRemoteDefrostCommands
    >
  >;
  compressorRPM$: Observable<Feature<IsNumberValueProperty, never>>;

  private ngUnsubscribe$ = new Subject<void>();
  private _refrigerantCircuitId: string;

  @Input()
  set refrigerantCircuitId(id: string) {
    this._refrigerantCircuitId = id;
    this.selectFeatures();
  }
  get refrigerantCircuitId(): string {
    return this._refrigerantCircuitId;
  }

  constructor(
    public devicesStore: DevicesStore,
    public authStore: AuthStore,
    private gtmService: GTMService,
    public gatewayStore: GatewayStore,
    private featureService: FeatureService,
    private scheduleService: ScheduleService,
    private timemanagerDialogService: TimemanagerDialogService,
    private unitsStore: UnitsStore,
    protected customTranslationsService: CustomTranslationsService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.primaryCircuitConfigurationPreVentilation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.primaryCircuitConfigurationPreVentilation
    );

    this.pvAvailable = pvSignal.pvAvailable;
    this.unitsStore.isTemperatureMetric$
      .pipe(take(1))
      .subscribe((isCelsius) => (this.isCelsius = isCelsius));
    this.offPeakSignal$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceOffPeakSignal
    );
    this.frostProtection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.frostProtection
    );
    this.spaceFrostProtectionConfiguration$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.spaceFrostProtectionConfiguration
    );
    this.pvSignal$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.devicePvSignal
    );
    this.spaceFrostProtection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.spaceFrostProtection
    );
    this.spaceFrostProtectionConfiguration$
      .pipe(
        filter((feature) => !!feature),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((feature) => {
        this.setValuesForTemperatureDropdown(feature);
      });

    this.primaryCircuitConfigurationPreVentilation$
      .pipe(
        filter((feature) => !!feature),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((feature) => {
        this.setValuesForMinuteDropdown(feature);
      });

    this.refrigerantCircuitGeneration$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.coolingCircuitType
    );

    this.typeElectronicExpansionValve$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.coolingCircuitEevType
    );

    this.ratedOutputCompressorStage$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerCompressor
    );

    this.heatingCoolingCircuitRemoteDefrost$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingCoolingCircuitsRemoteDefrost
    );

    this.airInletTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.primaryCircuitAirInletTemperatue
    );
    this.evaporationTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.evaporationTemperature
    );
    this.suctionGasTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.suctionGasTemperature
    );
    this.suctionGasTemperatureCompressor$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.compressorSuctionGasTemperature
    );
    this.lowPressureCompressor$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.compressorLowPressureSensor
    );

    this.hotGasTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.hotGasTemperatureSensorCompressor
    );
    this.liquidGasTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.liquidGasTemperatureSensorCompressor
    );
    this.hotGasTemperatureCompressor$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.hotGasTemperatureSensorCompresssor
    );
    this.highPressureCompressor$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.highPressureSensorCompressor
    );
    this.currentThermalPower$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.singleCompressorHeatProductionCurrent(this.refrigerantCircuitId)
    );
    this.currentCompressorSpeed$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.singleCompressorRelativeSpeedSetpoint(this.refrigerantCircuitId)
    );

    this.fanOneActualValue$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.Fan1ActualValue
    );
    this.fanTwoActualValue$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.Fan2ActualValue
    );
    this.electronicExpansionValveOneSetpoint$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.electronicExpansionValve1Setpoint
    );
    this.electronicExpansionValveOneActualValue$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.electronicExpansionValve1ActualValue
    );
    this.electronicExpansionValveTwoSetpoint$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.electronicExpansionValve2Setpoint
    );
    this.electronicExpansionValueTwoActualValue$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.electronicExpansionValve2ActualValue
    );

    this.compressorRPM$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsNumberValueProperty, never>(
        FeatureNames.compressorSpeedCurrent
      )
      .pipe(
        map((feature) => ({
          ...feature,
          properties: {
            ...feature?.properties,
            value: {
              ...feature?.properties?.value,
              value: feature?.properties?.value?.value * 60 || null,
            },
          },
        }))
      );
  }

  onTemperatureChange(value: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.spaceFrostProtectionConfiguration,
      this.isCelsius ? value : Math.round(convertFahrenheitToCelsius(value)),
      FeaturePropertyKey.value,
      FeatureActionFields.temperature,
      FeatureCommands.setTemperature
    );
  }

  onMinutesChange(value: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.primaryCircuitConfigurationPreVentilation,
      value,
      FeaturePropertyKey.value,
      FeatureActionFields.value,
      FeatureCommands.setValue
    );
  }

  onScheduleChange(
    event: { scheduleIds: number[]; periods: ScheduleEntry[] },
    feature: Feature<ScheduleProperties, ScheduleCommands>
  ): void {
    const schedule = this.scheduleService.getScheduleEntries(feature);
    const value = this.scheduleService.getScheduleValue(event.scheduleIds, event.periods, schedule);

    this.installationFeaturesStore.updateFeature(
      FeatureNames.heatingNoiseReductionSchedule,
      cloneDeep(value),
      FeaturePropertyKey.entries,
      FeatureActionFields.newSchedule,
      FeatureCommands.setSchedule
    );
  }

  onEditableByEndCustomerToggle(isChanged: boolean): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.heatingNoiseReduction,
      isChanged,
      FeaturePropertyKey.setChangeableByEndCustomer,
      FeatureActionFields.changeable,
      FeatureCommands.setChangeableByEndCustomer
    );
  }

  onEditMultipleDays(): void {
    this.timemanagerDialogService.openSelectDaysDialog();
  }

  calculateClocking(amountOfStarts: number, hoursRun: number): number {
    if (amountOfStarts) {
      return (hoursRun / amountOfStarts) * 60;
    }

    return NaN;
  }

  isClockingDataAvailable(amountOfStarts: number, hoursRun: number): boolean {
    return isNumber(amountOfStarts) && isNumber(hoursRun);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private setValuesForTemperatureDropdown(
    feature: Feature<IsNumberValueProperty, SpaceFrostProtectionCommands>
  ): void {
    const availableTemperatureFromFeature: {
      value: number;
      viewValue: string;
    }[] = [];
    const suffix = this.isCelsius ? SUFFIX.celsius : SUFFIX.fahrenheit;
    const constraints = feature?.commands?.setTemperature?.params?.temperature?.constraints;
    const featureValue = feature?.properties?.value;
    this.temperatureValueForRequest = featureValue.value;
    const { min, max, stepping } = constraints;
    this.temperatureForDropdownValue = this.isCelsius
      ? featureValue
      : {
          type: DcTypes.number,
          unit: suffix as string,
          value: convertCelsiusToFahrenheit(featureValue.value),
        };
    for (let temp = min; temp <= max; temp += stepping) {
      const value = this.isCelsius ? temp : convertCelsiusToFahrenheit(temp);
      availableTemperatureFromFeature.push({ value, viewValue: `${value} ${suffix}` });
    }
    this.availableTemperatureForDropdown = availableTemperatureFromFeature;
  }

  private setValuesForMinuteDropdown(
    feature: Feature<IsNumberValueProperty, HeatingCascadeTemperatureCommonSupplyCommands>
  ): void {
    const availableMinutesFromFeature: {
      value: number;
      viewValue: string;
    }[] = [];
    const constraints = feature?.commands?.setValue?.params?.value?.constraints;
    const featureValue = feature?.properties?.value;

    this.minutesForDropdownValue = featureValue;

    const { min, max, stepping } = constraints;
    for (let seconds = min; seconds <= max; seconds += stepping) {
      availableMinutesFromFeature.push({
        value: seconds,
        viewValue: `${seconds / 60} ${SUFFIX.minute}`,
      });
    }
    this.availableMinutesForDropdown = availableMinutesFromFeature;
  }

  private selectFeatures(): void {
    this.compressorStatistics$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.compressorStatistics
    );

    this.heatingNoiseReductionMode$ = this.installationFeaturesStore.getHeatingNoiseReductionMode();

    this.noiseReduction$ = this.installationFeaturesStore.selectInstallationDeviceFeatureByDeviceRole(
      FeatureNames.heatingNoiseReduction,
      DeviceRoles.InterfaceNoiseReduction
    );

    this.noiseReductionSchedule$ = this.installationFeaturesStore.selectInstallationDeviceFeatureByDeviceRole(
      FeatureNames.heatingNoiseReductionSchedule,
      DeviceRoles.InterfaceNoiseReduction
    );

    this.noiseReductionSchedule$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((feature) => !!feature?.properties?.entries?.value),
        tap((feature) => {
          this.scheduleService.createSchedule(feature);
        }),
        map((feature) => this.featureService.getScheduleModes(feature)),
        tap((modes) => this.scheduleService.createModes(modes))
      )
      .subscribe();

    this.programs$ = this.scheduleService.programs$;

    this.refrigerantCircuitStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.singleCompressorStatus(this.refrigerantCircuitId)
    );
  }

  onSelectRatedOutputCompressorStage(value: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.powerCompressor,
      value,
      FeaturePropertyKey.value,
      FeatureActionFields.value,
      FeatureCommands.setValue
    );
    this.gtmService.track(
      'onSelect',
      'Rated Output Compressor Stage Select',
      'refrigerantCircuitDetailsOverlay'
    );
  }
}
